/* WebComponentTabbedAccordion component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Listen, Prop, h } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import TabbedAccordionTemplate from './cwc-tabbed-accordion.template';
import { AbstractComponent, FontFaces } from '@cewe-designsystem/abstract';

// ResizeObserver
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';

const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;

/**
 * @slot action-left-icon - HTML element used as caption for the button to jump to the next tab e.g. an img or svg
 * @slot action-right-icon - HTML element used as caption for the button to jump to the previous tab e.g. an img or svg
 * @slot mobile-open-indicator-icon - HTML element used as open indicator icon for the accordion tab in mobile mode e.g. an img or svg
 */
@Component({
  tag: 'cwc-tabbed-accordion',
  styleUrl: './cwc-tabbed-accordion.scss',
  shadow: true,
})
export class TabbedAccordionComponent {
  /**
   * element
   */
  @FontFaces()
  @Element()
  element: HTMLElement;

  /**
   * id of this component
   */
  @Prop({ reflect: true }) dataId: string;

  /**
   * max height of initial component
   */
  maxInitialHeight: number;

  /**
   * indicator for hash update by click on tab
   */
  isHashSetByActiveTab: boolean = false;

  /**
   * tabs of this accordion
   */
  tabs: Element[];

  /**
   * handleTabHover
   * @param event
   */
  @Listen('mousemove', { target: 'window' })
  handleTabHover(event) {
    const oneLabelHovered = this.isOneLabelHovered(event),
      originalTarget = this.getOriginalTarget(event);

    if (oneLabelHovered && this.getActiveTabElement() !== originalTarget) {
      this.setUnderlineTabBy(originalTarget);
    } else if (!oneLabelHovered) {
      this.setUnderlineTabBy(this.getActiveTabElement());
    }
  }

  /**
   * handleTabClick
   * @param event
   */
  @Listen('click')
  handleTabClick(event) {
    if (this.eventTargetIsTab(event)) {
      this.setActiveTabBy(this.getOriginalTarget(event));
      this.setHashByActiveTab();
    }
  }

  /**
   * eventTargetIsTab
   * @param event
   * @private
   */
  private eventTargetIsTab(event) {
    return this.getOriginalTarget(event) && this.getOriginalTarget(event).tagName === 'LABEL';
  }

  /**
   * getOriginalTarget
   * @param event
   * @private
   */
  private getOriginalTarget(event) {
    return this.getHoveredLabel(event);
  }

  /**
   * getActiveTabElement
   * @private
   */
  private getActiveTabElement() {
    return this.element.shadowRoot.querySelector('label[data-active]') as HTMLElement;
  }

  /**
   * isOneLabelHovered
   * @param event
   * @private
   */
  private isOneLabelHovered(event) {
    let result = false;
    this.element.shadowRoot.querySelectorAll('label').forEach((label) => {
      const bounding = label.getBoundingClientRect();

      // is pointer in label bounding box?
      if (
        event.clientX >= bounding.left &&
        event.clientX <= bounding.right &&
        event.clientY >= bounding.top &&
        event.clientY <= bounding.bottom
      ) {
        result = true;
      }
    });

    return result;
  }

  /**
   * getHoveredLabel
   * @param event
   * @private
   */
  private getHoveredLabel(event) {
    let result;
    this.element.shadowRoot.querySelectorAll('label').forEach((label) => {
      const bounding = label.getBoundingClientRect();

      // is pointer in label bounding box?
      if (
        event.clientX >= bounding.left &&
        event.clientX <= bounding.right &&
        event.clientY >= bounding.top &&
        event.clientY <= bounding.bottom
      ) {
        result = label;
      }
    });

    return result;
  }

  /**
   * setUnderlineTabBy
   * @param label
   * @private
   */
  private setUnderlineTabBy(label: HTMLElement) {
    if (!label) return;

    const labelWidth = label.clientWidth,
      labelHeight = label.clientHeight,
      labelPositionLeft = label.offsetLeft,
      labelPositionTop = label.offsetTop,
      tabUnderline = this.element.shadowRoot.querySelector('hr');

    tabUnderline.style.width = labelWidth + 'px';
    tabUnderline.style.top = labelPositionTop + labelHeight + 'px';
    tabUnderline.style.left = labelPositionLeft + 'px';
  }

  /**
   * setActiveBy
   * @param label
   */
  setActiveTabBy(label: HTMLElement) {
    this.resetTabs();
    label.setAttribute('data-active', '');
    label.nextElementSibling.setAttribute('data-active', '');

    const labelFor = label.getAttribute('for'),
      inputForLabel = this.element.shadowRoot.querySelector('#' + labelFor);

    inputForLabel.setAttribute('checked', '');

    this.setUnderlineTabBy(label);
    this.setArrowsByLabel(label);
  }

  /**
   * getActiveTabByHash
   */
  getActiveTabByHash() {
    if (window.location.hash.length == 0) return;

    const knownLabel = Array.from(this.element.shadowRoot.querySelectorAll('label')).filter((label) => {
      return label.getAttribute('for') == window.location.hash.replace('#', '');
    });

    return knownLabel[0];
  }

  /**
   * setHashByActiveTab
   */
  setHashByActiveTab() {
    this.isHashSetByActiveTab = true;
    window.location.hash = '#' + this.getActiveTabElement().getAttribute('for');
  }

  /**
   * setArrowsByLabel
   * @param label
   * @private
   */
  private setArrowsByLabel(label: HTMLElement) {
    this.element.shadowRoot.querySelectorAll('li').forEach((arrow) => arrow.removeAttribute('data-hidden'));

    if (label.getAttribute('data-first') != null) {
      const arrowPrev = Array.from(this.element.shadowRoot.querySelectorAll('li')).shift();
      arrowPrev.setAttribute('data-hidden', '');
    }

    if (label.getAttribute('data-last') != null) {
      const arrowNext = Array.from(this.element.shadowRoot.querySelectorAll('li')).pop();
      arrowNext.setAttribute('data-hidden', '');
    }
  }

  /**
   * setTabHeightOnBiggestForDesktop
   * @private
   */
  private setTabHeightOnBiggestForDesktop() {
    let height = 0;
    /* eslint-disable-next-line no-magic-numbers */
    if (window.innerWidth > 1023) {
      this.element.shadowRoot.querySelectorAll('cwc-tabbed-accordion-tab').forEach((tab) => {
        height = height < tab.clientHeight ? tab.clientHeight : height;
      });
    } else {
      height = 0;
    }

    this.element.shadowRoot.querySelectorAll('cwc-tabbed-accordion-tab').forEach((tab) => {
      // @ts-expect-error migrated from ts-ignore
      tab.style.height = height == 0 ? 'auto' : height + 'px';
    });
  }

  /**
   * resetTabs
   * @private
   */
  private resetTabs() {
    this.element.shadowRoot.querySelectorAll('input').forEach((input) => input.removeAttribute('checked'));
    this.element.shadowRoot.querySelectorAll('label').forEach((label) => label.removeAttribute('data-active'));
    this.element.shadowRoot
      .querySelectorAll('cwc-tabbed-accordion-tab')
      .forEach((tab) => tab.removeAttribute('data-active'));
  }

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {}

  /**
   * Called every time the component is disconnected from the DOM, i.e., it calls be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {}

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {}

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {}

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {}

  /**
   * Called after every render().
   */
  componentDidRender() {
    const initialTab = this.getActiveTabByHash() || this.element.shadowRoot.querySelectorAll('label')[0];
    this.setActiveTabBy(initialTab);

    // resize observer to react on resize of this component
    const resizeObserver = new ResizeObserver(async () => {
      // recalculate underline position for active element
      const activeTab = this.getActiveTabElement();
      this.setUnderlineTabBy(activeTab);
      this.setTabHeightOnBiggestForDesktop();
    });

    resizeObserver.observe(this.element);

    // if a hashchange is fired
    window.addEventListener('hashchange', () => {
      // we check if this is a change made by active tab update in component, if not we continue
      if (!this.isHashSetByActiveTab) {
        // now we try to get a tab with the id that matches the hashcode
        const tab = this.getActiveTabByHash();
        if (tab) {
          // if we found a match by id for the hashcode, we activate this tab
          this.setActiveTabBy(tab);
        }
      }

      // here we ensure that the indicator for self updating a hashcode
      // after an active tab update is set to false if we hit the previous if case
      this.isHashSetByActiveTab = false;
    });

    // scroll tab in view if mobile breakpoint
    /* eslint-disable-next-line no-magic-numbers */
    if (window.innerWidth <= 1023) {
      // each tab gets a listener for content transition animation
      this.element.shadowRoot.querySelectorAll('cwc-tabbed-accordion-tab').forEach(function (tab) {
        // if the content transition is finished, we scroll to the end of it the smooth way
        tab.addEventListener('transitionstart', function (event) {
          const animatedTab = event.target as HTMLElement;
          // scroll only to tab if it is also active
          if (animatedTab.hasAttribute('data-active')) {
            // a timeout set to zero creates a thread for animation in browser and prevents stuttering
            setTimeout(() => {
              // scroll element smooth to top of window if scrollbar is not at the end
              animatedTab.previousElementSibling.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }, 0);
          }
        });
      });
    }
  }

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It is never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It is never called during the first render().
   */
  componentDidUpdate() {}

  /**
   * onClickNext
   */
  onClickNext() {
    const activeTab = this.getActiveTabElement();
    let nextSiblingTab = activeTab.nextElementSibling as HTMLElement;

    while (nextSiblingTab && nextSiblingTab.tagName.toLowerCase() != 'label') {
      nextSiblingTab = nextSiblingTab.nextElementSibling as HTMLElement;
    }

    if (!nextSiblingTab) {
      nextSiblingTab = activeTab;
    }

    this.setActiveTabBy(nextSiblingTab);
    this.setHashByActiveTab();
  }

  /**
   * onClickPrev
   */
  onClickPrev() {
    const activeTab = this.getActiveTabElement();
    let previousSiblingTab = activeTab.previousElementSibling as HTMLElement;

    while (previousSiblingTab && previousSiblingTab.tagName.toLowerCase() != 'label') {
      previousSiblingTab = previousSiblingTab.previousElementSibling as HTMLElement;
    }

    if (!previousSiblingTab) {
      previousSiblingTab = activeTab;
    }

    this.setActiveTabBy(previousSiblingTab);
    this.setHashByActiveTab();
  }

  /**
   * render
   */
  render() {
    const mobileIconTmpl: Element = this.element
      .querySelector<HTMLTemplateElement>('template#mobile-open-indicator-icon')
      ?.content.firstElementChild.cloneNode(true) as Element;

    this.tabs = Array.from(this.element.querySelectorAll('cwc-tabbed-accordion-tab'));

    return <TabbedAccordionTemplate controller={this} mobileIcon={mobileIconTmpl} />;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(TabbedAccordionComponent.prototype, AbstractComponent.prototype);

export interface TabbedAccordionComponent extends AbstractComponent {}
